import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";

export interface ICommunity {
  _id?: string;
  name: string;
  description: string;
  website: string;
  image: string;
  thumbnail?: string;
  isFeatured?: boolean;
  featuredImage?: string;
  balance?: number;
  totalRevenue?: number;
  members?: number;
  cities?: string[];
  showDonators?: boolean;
  order?: number;
  pinnedCities?: string[];
}

export interface ICommunityStats {
  donationsCount: number;
  totalAmountDonated: number;
  userCount: number;
}

interface IStore {
  /** State */
  communityStats: ICommunityStats | null;
  communities: ICommunity[];
  community: ICommunity | null;
  addedCommunity: any | null;
  updatedCommunity: any | null;
  deletedCommunity: any | null;
  hasNextPage: boolean;
  /** Callbacks */
  getCommunityStats: () => void;
  getCommunities: (page: number, limit: number) => void;
  getCommunity: (id: string) => void;
  addCommunity: (community: ICommunity) => void;
  updateCommunity: (id: string, community: ICommunity) => void;
  deleteCommunity: (id: string) => void;
  reorderCommunities: (items: { id: string; order: number }[]) => void;
  togglePin: (id: string, city: string, pinned: boolean) => void;
  clearAddedCommunity: () => void;
  clearUpdatedCommunity: () => void;
  clearDeletedCommunity: () => void;
}

export const useCommunities = create<IStore>()((set) => ({
  /** State */
  communityStats: null,
  communities: [],
  community: null,
  addedCommunity: null,
  updatedCommunity: null,
  deletedCommunity: null,
  hasNextPage: false,
  /** Callbacks */
  getCommunityStats: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("payments/community/stats");
      if (response?.data) {
        set({ communityStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching community stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getCommunities: async (page, limit) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `payments/community/paginated?page=${page}&limit=${limit}&dashboard=true`
      );
      if (response?.data) {
        // TEMPORARY debug logging - remove once the pin/order issue is
        // diagnosed. dashboard=true skips the city filter, so pin never
        // floats anything here - this should just reflect plain `order`.
        console.log(
          "[COMMUNITY_ORDER_DEBUG] dashboard getCommunities (page " +
            page +
            "):\n" +
            (response.data as ICommunity[])
              .map(
                (c, i) =>
                  `  ${i}: ${c.name} | order=${c.order} | pinnedCities=${JSON.stringify(
                    c.pinnedCities
                  )} | cities=${JSON.stringify(c.cities)}`
              )
              .join("\n")
        );
        set((prev) => ({
          hasNextPage: response.data.length >= limit,
          communities:
            page === 1
              ? response.data
              : [...prev.communities, ...response.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching communities", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getCommunity: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`payments/community/${id}`);
      if (response?.data) {
        set({ community: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching community", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addCommunity: async (community: any) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("payments/community", community);
      if (response?.data) {
        set({ addedCommunity: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on adding community", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateCommunity: async (id: string, community: any) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`payments/community/${id}`, community);
      if (response?.data) {
        set({ updatedCommunity: response.data });
      }
    } catch (error) {
      LOG.error("Error on updating community", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteCommunity: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`payments/community/${id}`);
      if (response?.data) {
        set((state) => ({
          deletedCommunity: response.data,
          communities: state.communities.filter(
            (community) => community._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting community", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  reorderCommunities: async (items) => {
    // Optimistic update, same pattern as Partner reorder - `order` is global
    // (not per-city), so this list is never city-filtered.
    set((state) => {
      const orderMap = new Map(items.map((i) => [i.id, i.order]));
      return {
        communities: state.communities.map((c) =>
          c._id && orderMap.has(c._id) ? { ...c, order: orderMap.get(c._id) } : c
        ),
      };
    });
    try {
      await axios.patch("payments/community/reorder", items);
    } catch (error) {
      LOG.error("Error on reordering communities", { error });
    }
  },
  togglePin: async (id, city, pinned) => {
    // Optimistic update on the single city being toggled, leaving this
    // community's pin state in every other city untouched.
    set((state) => ({
      communities: state.communities.map((c) => {
        if (c._id !== id) return c;
        const current = c.pinnedCities ?? [];
        const next = pinned
          ? Array.from(new Set([...current, city]))
          : current.filter((existing) => existing !== city);
        return { ...c, pinnedCities: next };
      }),
    }));
    try {
      // TEMPORARY debug logging - remove once the pin issue is diagnosed.
      // Quoted so a trailing space or casing mismatch against the city
      // string the app/backend resolve is visible, not silently trimmed.
      console.log(
        `[COMMUNITY_ORDER_DEBUG] dashboard togglePin: id=${id} city=${JSON.stringify(
          city
        )} pinned=${pinned}`
      );
      const response = await axios.patch(`payments/community/${id}/pin`, {
        city,
        pinned,
      });
      console.log(
        "[COMMUNITY_ORDER_DEBUG] dashboard togglePin response pinnedCities=" +
          JSON.stringify(response?.data?.pinnedCities)
      );
    } catch (error) {
      LOG.error("Error on toggling community pin", { error });
    }
  },
  clearAddedCommunity: () => {
    set({ addedCommunity: null });
  },
  clearUpdatedCommunity: () => {
    set({ updatedCommunity: null });
  },
  clearDeletedCommunity: () => {
    set({ deletedCommunity: null });
  },
}));
